/**
 * ============================================================
 * ⚠️  PROTECTED FILE - DO NOT MODIFY WITHOUT APPROVAL  ⚠️
 * ============================================================
 * This is the main Sales Dashboard shown at /home after login.
 * Layout and functionality have been approved by the client.
 * Any changes require explicit approval.
 * ============================================================
 */

import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { toast } from "sonner";
import { format, parseISO, startOfWeek, endOfWeek, addDays, isSameDay } from "date-fns";
import { 
  Search, Calendar as CalendarIcon, CheckSquare, Home, Building2, 
  Users, Clock, Plus, ChevronLeft, ChevronRight, Eye, MapPin,
  Phone, Mail, User, Briefcase, CheckCircle2, Circle, AlertCircle,
  MoreHorizontal, Trash2, Edit, X, LogOut
} from "lucide-react";

const APPOINTMENT_TYPES = [
  { value: "viewing", label: "Viewing", color: "bg-blue-500" },
  { value: "valuation", label: "Valuation", color: "bg-purple-500" },
  { value: "property_visit", label: "Property Visit", color: "bg-green-500" },
  { value: "client_meeting", label: "Client Meeting", color: "bg-orange-500" },
  { value: "phone_call", label: "Phone Call", color: "bg-yellow-500" },
  { value: "other", label: "Other", color: "bg-gray-500" }
];

const PRIORITY_OPTIONS = [
  { value: "urgent", label: "Urgent", color: "text-red-600 bg-red-50" },
  { value: "high", label: "High", color: "text-orange-600 bg-orange-50" },
  { value: "medium", label: "Medium", color: "text-blue-600 bg-blue-50" },
  { value: "low", label: "Low", color: "text-gray-600 bg-gray-50" }
];

export default function HomeDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  
  // Diary state
  const [appointments, setAppointments] = useState([]);
  const [weekOffset, setWeekOffset] = useState(0);
  const [diaryView, setDiaryView] = useState("me"); // "me" or "office"
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState(null);
  
  // To-do state
  const [todos, setTodos] = useState([]);
  const [showTodoModal, setShowTodoModal] = useState(false);
  const [editingTodo, setEditingTodo] = useState(null);
  
  // Staff list for office view
  const [staffList, setStaffList] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [selectedDepartment, setSelectedDepartment] = useState("all");
  
  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };
  
  // Get current user
  const userStr = localStorage.getItem("user");
  const user = userStr ? JSON.parse(userStr) : null;
  
  const getInitials = (name) => {
    if (!name) return "U";
    return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  };
  
  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/login");
  };
  
  // Fetch dashboard data
  useEffect(() => {
    fetchDashboardData();
  }, []);
  
  // Fetch appointments when week or view changes
  useEffect(() => {
    fetchAppointments();
  }, [weekOffset, diaryView, selectedStaff]);
  
  const fetchDashboardData = async () => {
    try {
      const [summaryRes, todosRes, staffRes] = await Promise.all([
        fetch(`${API}/dashboard/summary`, { headers }),
        fetch(`${API}/dashboard/todos`, { headers }),
        fetch(`${API}/dashboard/staff`, { headers })
      ]);
      
      if (summaryRes.ok) {
        const data = await summaryRes.json();
        setSummary(data);
      }
      
      if (todosRes.ok) {
        const data = await todosRes.json();
        setTodos(data.todos || []);
      }
      
      if (staffRes.ok) {
        const data = await staffRes.json();
        setStaffList(data.staff || []);
      }
    } catch (error) {
      console.error("Error fetching dashboard:", error);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchAppointments = async () => {
    try {
      const userFilter = diaryView === "me" ? "me" : (selectedStaff || "");
      const res = await fetch(
        `${API}/dashboard/appointments/week?week_offset=${weekOffset}&user_filter=${userFilter}`,
        { headers }
      );
      if (res.ok) {
        const data = await res.json();
        setAppointments(data.appointments || []);
      }
    } catch (error) {
      console.error("Error fetching appointments:", error);
    }
  };
  
  // Search handler with debounce
  useEffect(() => {
    if (searchQuery.length < 2) {
      setSearchResults(null);
      return;
    }
    
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await fetch(
          `${API}/dashboard/search?q=${encodeURIComponent(searchQuery)}`,
          { headers }
        );
        if (res.ok) {
          const data = await res.json();
          setSearchResults(data);
        }
      } catch (error) {
        console.error("Search error:", error);
      } finally {
        setSearching(false);
      }
    }, 300);
    
    return () => clearTimeout(timer);
  }, [searchQuery]);
  
  // Week navigation
  const currentWeekStart = useMemo(() => {
    const today = new Date();
    const start = startOfWeek(today, { weekStartsOn: 1 });
    return addDays(start, weekOffset * 7);
  }, [weekOffset]);
  
  const weekDays = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => addDays(currentWeekStart, i));
  }, [currentWeekStart]);
  
  // Group appointments by day
  const appointmentsByDay = useMemo(() => {
    const grouped = {};
    weekDays.forEach(day => {
      const dateKey = format(day, "yyyy-MM-dd");
      grouped[dateKey] = appointments.filter(apt => 
        apt.start_time?.startsWith(dateKey)
      );
    });
    return grouped;
  }, [appointments, weekDays]);
  
  // Todo handlers
  const handleCompleteTodo = async (todoId) => {
    try {
      const res = await fetch(`${API}/dashboard/todos/${todoId}/complete`, {
        method: "PUT",
        headers
      });
      if (res.ok) {
        setTodos(todos.filter(t => t.todo_id !== todoId));
        toast.success("Task completed!");
        fetchDashboardData();
      }
    } catch (error) {
      toast.error("Failed to complete task");
    }
  };
  
  const handleDeleteTodo = async (todoId) => {
    try {
      const res = await fetch(`${API}/dashboard/todos/${todoId}`, {
        method: "DELETE",
        headers
      });
      if (res.ok) {
        setTodos(todos.filter(t => t.todo_id !== todoId));
        toast.success("Task deleted");
      }
    } catch (error) {
      toast.error("Failed to delete task");
    }
  };
  
  // Appointment handlers
  const handleDeleteAppointment = async (appointmentId) => {
    try {
      const res = await fetch(`${API}/dashboard/appointments/${appointmentId}`, {
        method: "DELETE",
        headers
      });
      if (res.ok) {
        fetchAppointments();
        toast.success("Appointment cancelled");
      }
    } catch (error) {
      toast.error("Failed to cancel appointment");
    }
  };
  
  const getAppointmentTypeInfo = (type) => {
    return APPOINTMENT_TYPES.find(t => t.value === type) || APPOINTMENT_TYPES[5];
  };
  
  const getPriorityInfo = (priority) => {
    return PRIORITY_OPTIONS.find(p => p.value === priority) || PRIORITY_OPTIONS[2];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-800"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="home-dashboard">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <h1 className="text-xl font-semibold text-slate-900">Dashboard</h1>
              {summary?.user && (
                <span className="text-sm text-slate-500">
                  Welcome back, {summary.user.name}
                </span>
              )}
            </div>
            
            {/* Global Search */}
            <div className="relative w-96">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                data-testid="global-search-input"
                placeholder="Search clients, properties, addresses..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-slate-50 border-slate-200"
              />
              
              {/* Search Results Dropdown */}
              {searchResults && searchQuery.length >= 2 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-lg border border-slate-200 max-h-96 overflow-y-auto z-50">
                  {searchResults.totals?.total === 0 ? (
                    <div className="p-4 text-center text-slate-500">
                      No results found for "{searchQuery}"
                    </div>
                  ) : (
                    <>
                      {/* Clients */}
                      {searchResults.clients?.length > 0 && (
                        <div className="p-2 border-b">
                          <div className="px-2 py-1 text-xs font-medium text-slate-500 uppercase">
                            Clients ({searchResults.clients.length})
                          </div>
                          {searchResults.clients.map((client) => (
                            <button
                              key={client.id}
                              onClick={() => {
                                navigate(client.link);
                                setSearchQuery("");
                                setSearchResults(null);
                              }}
                              className="w-full flex items-center gap-3 p-2 hover:bg-slate-50 rounded text-left"
                            >
                              <User className="h-4 w-4 text-slate-400" />
                              <div>
                                <div className="font-medium text-slate-900">{client.name}</div>
                                <div className="text-xs text-slate-500 flex items-center gap-2">
                                  {client.email && <span>{client.email}</span>}
                                  <Badge variant="outline" className="text-xs">{client.role}</Badge>
                                </div>
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                      
                      {/* Sales Properties */}
                      {searchResults.sales_properties?.length > 0 && (
                        <div className="p-2 border-b">
                          <div className="px-2 py-1 text-xs font-medium text-slate-500 uppercase">
                            Sales ({searchResults.sales_properties.length})
                          </div>
                          {searchResults.sales_properties.map((prop) => (
                            <button
                              key={prop.id}
                              onClick={() => {
                                navigate(prop.link);
                                setSearchQuery("");
                                setSearchResults(null);
                              }}
                              className="w-full flex items-center gap-3 p-2 hover:bg-slate-50 rounded text-left"
                            >
                              <Home className="h-4 w-4 text-blue-500" />
                              <div className="flex-1">
                                <div className="font-medium text-slate-900">{prop.address}</div>
                                <div className="text-xs text-slate-500 flex items-center gap-2">
                                  <span>{prop.postcode}</span>
                                  {prop.price && <span>£{prop.price.toLocaleString()}</span>}
                                </div>
                              </div>
                              <Badge variant="outline" className="text-xs">{prop.status}</Badge>
                            </button>
                          ))}
                        </div>
                      )}
                      
                      {/* Lettings Properties */}
                      {searchResults.lettings_properties?.length > 0 && (
                        <div className="p-2">
                          <div className="px-2 py-1 text-xs font-medium text-slate-500 uppercase">
                            Lettings ({searchResults.lettings_properties.length})
                          </div>
                          {searchResults.lettings_properties.map((prop) => (
                            <button
                              key={prop.id}
                              onClick={() => {
                                navigate(prop.link);
                                setSearchQuery("");
                                setSearchResults(null);
                              }}
                              className="w-full flex items-center gap-3 p-2 hover:bg-slate-50 rounded text-left"
                            >
                              <Building2 className="h-4 w-4 text-green-500" />
                              <div className="flex-1">
                                <div className="font-medium text-slate-900">{prop.address}</div>
                                <div className="text-xs text-slate-500 flex items-center gap-2">
                                  <span>{prop.postcode}</span>
                                  {prop.rent && <span>£{prop.rent}/pcm</span>}
                                </div>
                              </div>
                              <Badge variant="outline" className="text-xs">{prop.status}</Badge>
                            </button>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
            
            <div className="flex items-center gap-2">
              {/* Role-specific quick actions */}
              {(user?.role === 'inventory_clerk') && (
                <Button onClick={() => navigate("/inventory")} className="bg-purple-600 hover:bg-purple-700">
                  <Briefcase className="h-4 w-4 mr-2" />
                  My Inventories
                </Button>
              )}
              {(user?.role === 'sales_negotiator' || user?.role === 'admin' || user?.role === 'director') && (
                <Button variant="outline" onClick={() => navigate("/progression")}>
                  <Home className="h-4 w-4 mr-2" />
                  Sales Pipeline
                </Button>
              )}
              {(user?.role === 'lettings_negotiator' || user?.role === 'lettings_manager') && (
                <Button onClick={() => navigate("/admin/lettings")} className="bg-green-600 hover:bg-green-700">
                  <Building2 className="h-4 w-4 mr-2" />
                  My Lettings
                </Button>
              )}
              {(user?.role !== 'inventory_clerk' && user?.role !== 'lettings_negotiator' && user?.role !== 'lettings_manager') && (
                <Button variant="outline" onClick={() => navigate("/sales")}>
                  <Home className="h-4 w-4 mr-2" />
                  Sales
                </Button>
              )}
              {/* Lettings button - always show for all roles except inventory_clerk */}
              {(user?.role !== 'inventory_clerk') && (
                <Button variant="outline" onClick={() => navigate("/admin/lettings")}>
                  <Building2 className="h-4 w-4 mr-2" />
                  Lettings
                </Button>
              )}
              
              {/* User Profile Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="ml-2 gap-2" data-testid="user-menu-btn">
                    <Avatar className="h-6 w-6">
                      <AvatarFallback className="bg-blue-600 text-white text-xs">
                        {getInitials(user?.name)}
                      </AvatarFallback>
                    </Avatar>
                    <span className="hidden md:inline">{user?.name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-medium">{user?.name}</p>
                    <p className="text-xs text-slate-500">{user?.title || user?.role?.replace("_", " ")}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/my-profile")} data-testid="my-profile-link">
                    <User className="h-4 w-4 mr-2" />
                    My Profile
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                    <LogOut className="h-4 w-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Role-specific welcome banner */}
        {user?.role === 'inventory_clerk' && (
          <Card className="mb-6 bg-gradient-to-r from-purple-600 to-purple-700 text-white border-0">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold">Welcome back, {user?.name?.split(' ')[0]}!</h2>
                  <p className="text-purple-200">Inventory Clerk Dashboard</p>
                </div>
                <Button onClick={() => navigate("/inventory")} className="bg-white text-purple-700 hover:bg-purple-50">
                  <Briefcase className="h-4 w-4 mr-2" />
                  Go to Inventories
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
        
        {(user?.role === 'lettings_negotiator' || user?.role === 'lettings_manager') && (
          <Card className="mb-6 bg-gradient-to-r from-green-600 to-green-700 text-white border-0">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold">Welcome back, {user?.name?.split(' ')[0]}!</h2>
                  <p className="text-green-200">Lettings {user?.role === 'lettings_manager' ? 'Manager' : 'Negotiator'} Dashboard</p>
                </div>
                <Button onClick={() => navigate("/admin/lettings")} className="bg-white text-green-700 hover:bg-green-50">
                  <Building2 className="h-4 w-4 mr-2" />
                  View Lettings
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
        
        {user?.title?.toLowerCase().includes('progressor') && (
          <Card className="mb-6 bg-gradient-to-r from-blue-600 to-blue-700 text-white border-0">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold">Welcome back, {user?.name?.split(' ')[0]}!</h2>
                  <p className="text-blue-200">Sales Progressor Dashboard</p>
                </div>
                <Button onClick={() => navigate("/sales/progressor")} className="bg-white text-blue-700 hover:bg-blue-50">
                  <Home className="h-4 w-4 mr-2" />
                  My Pipeline
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
        
        {user?.title?.toLowerCase().includes('finance') && (
          <Card className="mb-6 bg-gradient-to-r from-amber-600 to-amber-700 text-white border-0">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold">Welcome back, {user?.name?.split(' ')[0]}!</h2>
                  <p className="text-amber-200">Finance Manager Dashboard</p>
                </div>
                <Button onClick={() => navigate("/admin/accounts")} className="bg-white text-amber-700 hover:bg-amber-50">
                  <Briefcase className="h-4 w-4 mr-2" />
                  Client Accounts
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Quick Access Cards - Clean & Simple */}
        {/* Show different cards for Sales Progressor (Tracey) vs others */}
        {(user?.email === 'tracey@goodacresresidential.co.uk' || user?.title?.toLowerCase().includes('progressor')) ? (
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            {/* Diary Card */}
            <Card 
              className="bg-gradient-to-br from-blue-500 to-blue-600 text-white cursor-pointer hover:shadow-lg transition-all hover:scale-[1.02]"
              onClick={() => navigate("/diary")}
            >
              <CardContent className="p-5">
                <CalendarIcon className="h-8 w-8 mb-3 opacity-80" />
                <h3 className="text-lg font-bold">Diary</h3>
                <p className="text-blue-100 text-sm">{summary?.stats?.today_appointments || 0} today</p>
              </CardContent>
            </Card>
            
            {/* Sales Card */}
            <Card 
              className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white cursor-pointer hover:shadow-lg transition-all hover:scale-[1.02]"
              onClick={() => navigate("/sales")}
            >
              <CardContent className="p-5">
                <Home className="h-8 w-8 mb-3 opacity-80" />
                <h3 className="text-lg font-bold">Sales</h3>
                <p className="text-emerald-100 text-sm">{summary?.stats?.active_sales || 0} active</p>
              </CardContent>
            </Card>
            
            {/* Sales Progression Card */}
            <Card 
              className="bg-gradient-to-br from-orange-500 to-orange-600 text-white cursor-pointer hover:shadow-lg transition-all hover:scale-[1.02]"
              onClick={() => navigate("/progression")}
            >
              <CardContent className="p-5">
                <CheckSquare className="h-8 w-8 mb-3 opacity-80" />
                <h3 className="text-lg font-bold">Sales Progression</h3>
                <p className="text-orange-100 text-sm">Pipeline & SSTC</p>
              </CardContent>
            </Card>
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {/* Diary Card */}
            <Card 
              className="bg-gradient-to-br from-blue-500 to-blue-600 text-white cursor-pointer hover:shadow-lg transition-all hover:scale-[1.02]"
              onClick={() => navigate("/diary")}
            >
              <CardContent className="p-5">
                <CalendarIcon className="h-8 w-8 mb-3 opacity-80" />
                <h3 className="text-lg font-bold">Diary</h3>
                <p className="text-blue-100 text-sm">{summary?.stats?.today_appointments || 0} today</p>
              </CardContent>
            </Card>
            
            {/* Sales Card */}
            <Card 
              className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white cursor-pointer hover:shadow-lg transition-all hover:scale-[1.02]"
              onClick={() => navigate("/sales")}
            >
              <CardContent className="p-5">
                <Home className="h-8 w-8 mb-3 opacity-80" />
                <h3 className="text-lg font-bold">Sales</h3>
                <p className="text-emerald-100 text-sm">{summary?.stats?.active_sales || 0} active</p>
              </CardContent>
            </Card>
            
            {/* Lettings Card */}
            <Card 
              className="bg-gradient-to-br from-purple-500 to-purple-600 text-white cursor-pointer hover:shadow-lg transition-all hover:scale-[1.02]"
              onClick={() => navigate("/admin/lettings")}
            >
              <CardContent className="p-5">
                <Building2 className="h-8 w-8 mb-3 opacity-80" />
                <h3 className="text-lg font-bold">Lettings</h3>
                <p className="text-purple-100 text-sm">{summary?.stats?.active_lettings || 0} active</p>
              </CardContent>
            </Card>
            
            {/* Sales Progression Card */}
            <Card 
              className="bg-gradient-to-br from-orange-500 to-orange-600 text-white cursor-pointer hover:shadow-lg transition-all hover:scale-[1.02]"
              onClick={() => navigate("/progression")}
            >
              <CardContent className="p-5">
                <CheckSquare className="h-8 w-8 mb-3 opacity-80" />
                <h3 className="text-lg font-bold">Sales Progression</h3>
                <p className="text-orange-100 text-sm">Pipeline & SSTC</p>
              </CardContent>
            </Card>
          </div>
        )}
        
        {/* Small Stats Row - Hide viewings for Sales Progressor */}
        <div className={`grid ${(user?.email === 'tracey@goodacresresidential.co.uk' || user?.title?.toLowerCase()?.includes('progressor')) ? 'grid-cols-2' : 'grid-cols-3'} gap-3 mb-6`}>
          <Card className="bg-white">
            <CardContent className="p-3 flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500">Pending Tasks</p>
                <p className="text-xl font-bold">{summary?.stats?.pending_todos || 0}</p>
              </div>
              <CheckSquare className="h-6 w-6 text-orange-400" />
            </CardContent>
          </Card>
          <Card className="bg-white">
            <CardContent className="p-3 flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500">Overdue</p>
                <p className="text-xl font-bold text-red-600">{summary?.stats?.overdue_todos || 0}</p>
              </div>
              <AlertCircle className="h-6 w-6 text-red-400" />
            </CardContent>
          </Card>
          {!(user?.email === 'tracey@goodacresresidential.co.uk' || user?.title?.toLowerCase()?.includes('progressor')) && (
            <Card className="bg-white">
              <CardContent className="p-3 flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-500">Week Viewings</p>
                  <p className="text-xl font-bold">{summary?.stats?.week_viewings || 0}</p>
                </div>
                <Eye className="h-6 w-6 text-cyan-400" />
              </CardContent>
            </Card>
          )}
        </div>
        
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left Column - Diary */}
          <div className="lg:col-span-2 space-y-6" id="diary-section">
            <Card className="bg-white">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <CalendarIcon className="h-5 w-5" />
                    Diary
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <Tabs value={diaryView} onValueChange={(v) => { setDiaryView(v); setSelectedDepartment("all"); }}>
                      <TabsList className="h-8">
                        <TabsTrigger value="me" className="text-xs px-3">My Diary</TabsTrigger>
                        <TabsTrigger value="office" className="text-xs px-3">Office Diary</TabsTrigger>
                      </TabsList>
                    </Tabs>
                    <Button
                      size="sm"
                      onClick={() => {
                        setEditingAppointment(null);
                        setShowAppointmentModal(true);
                      }}
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add
                    </Button>
                  </div>
                </div>
                
                {/* Office view department filter */}
                {diaryView === "office" && (
                  <div className="mt-3 flex items-center gap-3">
                    <Tabs value={selectedDepartment || "all"} onValueChange={setSelectedDepartment}>
                      <TabsList className="h-8">
                        <TabsTrigger value="all" className="text-xs px-3">All</TabsTrigger>
                        <TabsTrigger value="sales" className="text-xs px-3">Sales</TabsTrigger>
                        <TabsTrigger value="lettings" className="text-xs px-3">Lettings</TabsTrigger>
                      </TabsList>
                    </Tabs>
                    {staffList.length > 0 && (
                      <Select value={selectedStaff || "all"} onValueChange={(v) => setSelectedStaff(v === "all" ? null : v)}>
                        <SelectTrigger className="w-40 h-8">
                          <SelectValue placeholder="All staff" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Staff</SelectItem>
                          {staffList
                            .filter(s => {
                              if (selectedDepartment === "all") return true;
                              if (selectedDepartment === "sales") return s.role?.includes("sales") || s.title?.toLowerCase().includes("sales");
                              if (selectedDepartment === "lettings") return s.role?.includes("lettings");
                              return true;
                            })
                            .map((staff) => (
                              <SelectItem key={staff.user_id} value={staff.user_id}>
                                {staff.name}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                )}
              </CardHeader>
              <CardContent>
                {/* Week Navigation */}
                <div className="flex items-center justify-between mb-4">
                  <Button variant="ghost" size="sm" onClick={() => setWeekOffset(weekOffset - 1)}>
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <div className="text-sm font-medium">
                    {format(currentWeekStart, "d MMM")} - {format(addDays(currentWeekStart, 6), "d MMM yyyy")}
                  </div>
                  <div className="flex gap-1">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => setWeekOffset(0)}
                      disabled={weekOffset === 0}
                    >
                      Today
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setWeekOffset(weekOffset + 1)}>
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                
                {/* Week View with Time Slots */}
                <div className="overflow-x-auto max-h-80">
                  <div className="grid grid-cols-8 gap-0 min-w-[800px]">
                    {/* Time column header */}
                    <div className="sticky left-0 bg-white z-10 border-r">
                      <div className="h-12 border-b flex items-center justify-center text-xs font-medium text-slate-500">
                        Time
                      </div>
                      {/* Time slots from 8:00am to 8:00pm (hourly) */}
                      {(() => {
                        const slots = [];
                        for (let h = 8; h <= 20; h++) {
                          slots.push({ h, m: 0 });
                        }
                        return slots.map(({ h }, i) => (
                          <div key={i} className="h-10 border-b flex items-center justify-end pr-2 text-xs text-slate-400">
                            {h.toString().padStart(2, '0')}:00
                          </div>
                        ));
                      })()}
                    </div>
                    
                    {/* Day columns */}
                    {weekDays.map((day) => {
                      const dateKey = format(day, "yyyy-MM-dd");
                      const dayAppointments = appointmentsByDay[dateKey] || [];
                      const isToday = isSameDay(day, new Date());
                      
                      // Build hourly time slots (8am - 8pm)
                      const slots = [];
                      for (let h = 8; h <= 20; h++) {
                        slots.push({ h, m: 0 });
                      }
                      
                      return (
                        <div key={dateKey} className={`${isToday ? "bg-blue-50" : ""}`}>
                          {/* Day header */}
                          <div className={`h-12 border-b flex flex-col items-center justify-center ${isToday ? "bg-blue-100" : "bg-slate-50"}`}>
                            <div className={`text-xs font-medium ${isToday ? "text-blue-600" : "text-slate-500"}`}>
                              {format(day, "EEE")}
                            </div>
                            <div className={`text-lg font-bold ${isToday ? "text-blue-700" : "text-slate-700"}`}>
                              {format(day, "d")}
                            </div>
                          </div>
                          
                          {/* Time slots - hourly */}
                          {slots.map(({ h }, i) => {
                            const slotStart = `${h.toString().padStart(2, '0')}:00`;
                            const slotEnd = `${(h + 1).toString().padStart(2, '0')}:00`;
                            
                            // Find appointments within this hour (including 30-min ones)
                            const slotAppts = dayAppointments.filter(apt => {
                              if (!apt.start_time) return false;
                              const aptTime = apt.start_time.includes('T') 
                                ? apt.start_time.split('T')[1]?.substring(0, 5)
                                : apt.start_time.substring(0, 5);
                              return aptTime >= slotStart && aptTime < slotEnd;
                            });
                            
                            return (
                              <div key={i} className="h-10 border-b border-r border-slate-100 relative overflow-hidden">
                                {slotAppts.length > 0 && (
                                  <div className="absolute inset-0 flex flex-col gap-0.5 p-0.5">
                                    {slotAppts.slice(0, 2).map((apt, idx) => {
                                      const typeInfo = getAppointmentTypeInfo(apt.appointment_type);
                                      const aptTime = apt.start_time.includes('T') 
                                        ? apt.start_time.split('T')[1]?.substring(0, 5)
                                        : apt.start_time.substring(0, 5);
                                      return (
                                        <div
                                          key={apt.appointment_id}
                                          className={`${typeInfo.color} text-white text-[10px] px-1 rounded-sm cursor-pointer hover:opacity-80 truncate flex-1`}
                                          onClick={() => {
                                            setEditingAppointment(apt);
                                            setShowAppointmentModal(true);
                                          }}
                                          title={`${apt.title} - ${aptTime}`}
                                        >
                                          <span className="font-medium">{aptTime}</span> {apt.title?.substring(0, 10)}
                                        </div>
                                      );
                                    })}
                                    {slotAppts.length > 2 && (
                                      <div className="text-[9px] text-slate-500 text-center">
                                        +{slotAppts.length - 2} more
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                </div>
                
                {/* Quick appointment summary below */}
                <div className="mt-4 grid grid-cols-7 gap-1">
                  {weekDays.map((day) => {
                    const dateKey = format(day, "yyyy-MM-dd");
                    const dayAppointments = appointmentsByDay[dateKey] || [];
                    const isToday = isSameDay(day, new Date());
                    
                    return (
                      <div key={dateKey + "_summary"} className="text-center">
                        {dayAppointments.length > 0 && (
                          <Badge variant={isToday ? "default" : "secondary"} className="text-xs">
                            {dayAppointments.length} appt{dayAppointments.length > 1 ? 's' : ''}
                          </Badge>
                        )}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
            
            {/* Recent Activity */}
            <Card className="bg-white">
              <CardHeader>
                <CardTitle className="text-base">Recent Activity</CardTitle>
              </CardHeader>
              <CardContent>
                {summary?.recent_activity?.length > 0 ? (
                  <div className="space-y-3">
                    {summary.recent_activity.map((activity, idx) => (
                      <div 
                        key={idx}
                        className="flex items-center gap-3 p-2 rounded hover:bg-slate-50 cursor-pointer"
                        onClick={() => navigate(`/sales/case/${activity.id}`)}
                      >
                        <Home className="h-4 w-4 text-slate-400" />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm truncate">{activity.title}</div>
                          <div className="text-xs text-slate-500">{activity.subtitle}</div>
                        </div>
                        <Badge variant="outline" className="text-xs">{activity.status}</Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-500 text-center py-4">No recent activity</p>
                )}
              </CardContent>
            </Card>
          </div>
          
          {/* Right Column - To-Do List */}
          <div className="space-y-6">
            <Card className="bg-white">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <CheckSquare className="h-5 w-5" />
                    To-Do List
                  </CardTitle>
                  <Button
                    size="sm"
                    onClick={() => {
                      setEditingTodo(null);
                      setShowTodoModal(true);
                    }}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {todos.length > 0 ? (
                  <div className="space-y-2">
                    {todos.map((todo) => {
                      const priorityInfo = getPriorityInfo(todo.priority);
                      const isOverdue = todo.due_date && new Date(todo.due_date) < new Date();
                      
                      return (
                        <div 
                          key={todo.todo_id}
                          className={`p-3 rounded-lg border ${isOverdue ? "border-red-200 bg-red-50" : "border-slate-200"}`}
                        >
                          <div className="flex items-start gap-2">
                            <button
                              onClick={() => handleCompleteTodo(todo.todo_id)}
                              className="mt-0.5 text-slate-400 hover:text-green-500"
                            >
                              <Circle className="h-4 w-4" />
                            </button>
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-sm">{todo.title}</div>
                              {todo.description && (
                                <div className="text-xs text-slate-500 mt-1 line-clamp-2">
                                  {todo.description}
                                </div>
                              )}
                              <div className="flex items-center gap-2 mt-2">
                                <Badge className={`text-xs ${priorityInfo.color}`}>
                                  {priorityInfo.label}
                                </Badge>
                                {todo.due_date && (
                                  <span className={`text-xs ${isOverdue ? "text-red-600 font-medium" : "text-slate-500"}`}>
                                    Due: {format(parseISO(todo.due_date), "d MMM")}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="flex gap-1">
                              <button
                                onClick={() => {
                                  setEditingTodo(todo);
                                  setShowTodoModal(true);
                                }}
                                className="p-1 text-slate-400 hover:text-slate-600"
                              >
                                <Edit className="h-3 w-3" />
                              </button>
                              <button
                                onClick={() => handleDeleteTodo(todo.todo_id)}
                                className="p-1 text-slate-400 hover:text-red-500"
                              >
                                <Trash2 className="h-3 w-3" />
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-2 opacity-50" />
                    <p className="text-sm text-slate-500">All caught up!</p>
                    <p className="text-xs text-slate-400">No pending tasks</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
      
      {/* Appointment Modal */}
      <AppointmentModal
        open={showAppointmentModal}
        onClose={() => {
          setShowAppointmentModal(false);
          setEditingAppointment(null);
        }}
        appointment={editingAppointment}
        onSave={() => {
          fetchAppointments();
          fetchDashboardData();
        }}
        onDelete={handleDeleteAppointment}
      />
      
      {/* Todo Modal */}
      <TodoModal
        open={showTodoModal}
        onClose={() => {
          setShowTodoModal(false);
          setEditingTodo(null);
        }}
        todo={editingTodo}
        onSave={() => {
          fetchDashboardData();
          fetch(`${API}/dashboard/todos`, { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } })
            .then(res => res.json())
            .then(data => setTodos(data.todos || []));
        }}
      />
    </div>
  );
}

// Appointment Modal Component
function AppointmentModal({ open, onClose, appointment, onSave, onDelete }) {
  const [formData, setFormData] = useState({
    title: "",
    appointment_type: "other",
    start_time: "",
    duration_minutes: 30,
    client_name: "",
    address: "",
    notes: ""
  });
  const [saving, setSaving] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedTime, setSelectedTime] = useState("09:00");
  
  const token = localStorage.getItem("token");
  
  useEffect(() => {
    if (appointment) {
      setFormData({
        title: appointment.title || "",
        appointment_type: appointment.appointment_type || "other",
        start_time: appointment.start_time || "",
        duration_minutes: appointment.duration_minutes || 30,
        client_name: appointment.client_name || "",
        address: appointment.address || "",
        notes: appointment.notes || ""
      });
      if (appointment.start_time) {
        const dt = parseISO(appointment.start_time);
        setSelectedDate(dt);
        setSelectedTime(format(dt, "HH:mm"));
      }
    } else {
      setFormData({
        title: "",
        appointment_type: "other",
        start_time: "",
        duration_minutes: 30,
        client_name: "",
        address: "",
        notes: ""
      });
      setSelectedDate(new Date());
      setSelectedTime("09:00");
    }
  }, [appointment, open]);
  
  const handleSave = async () => {
    if (!formData.title) {
      toast.error("Please enter a title");
      return;
    }
    
    setSaving(true);
    try {
      // Combine date and time
      const dateStr = format(selectedDate, "yyyy-MM-dd");
      const startTime = `${dateStr}T${selectedTime}:00`;
      
      const payload = {
        ...formData,
        start_time: startTime
      };
      
      const url = appointment 
        ? `${API}/dashboard/appointments/${appointment.appointment_id}`
        : `${API}/dashboard/appointments`;
      
      const res = await fetch(url, {
        method: appointment ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      
      if (res.ok) {
        toast.success(appointment ? "Appointment updated" : "Appointment created");
        onSave();
        onClose();
      } else {
        toast.error("Failed to save appointment");
      }
    } catch (error) {
      toast.error("Error saving appointment");
    } finally {
      setSaving(false);
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {appointment ? "Edit Appointment" : "New Appointment"}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div>
            <Label>Title *</Label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="e.g., Viewing at 123 High Street"
            />
          </div>
          
          <div>
            <Label>Type</Label>
            <Select 
              value={formData.appointment_type} 
              onValueChange={(v) => setFormData({ ...formData, appointment_type: v })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {APPOINTMENT_TYPES.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${type.color}`} />
                      {type.label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {format(selectedDate, "d MMM yyyy")}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={(d) => d && setSelectedDate(d)}
                  />
                </PopoverContent>
              </Popover>
            </div>
            
            <div>
              <Label>Time</Label>
              <Input
                type="time"
                value={selectedTime}
                onChange={(e) => setSelectedTime(e.target.value)}
              />
            </div>
          </div>
          
          <div>
            <Label>Duration (minutes)</Label>
            <Select 
              value={String(formData.duration_minutes)} 
              onValueChange={(v) => setFormData({ ...formData, duration_minutes: parseInt(v) })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="15">15 mins</SelectItem>
                <SelectItem value="30">30 mins</SelectItem>
                <SelectItem value="45">45 mins</SelectItem>
                <SelectItem value="60">1 hour</SelectItem>
                <SelectItem value="90">1.5 hours</SelectItem>
                <SelectItem value="120">2 hours</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label>Client Name</Label>
            <Input
              value={formData.client_name}
              onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
              placeholder="Optional"
            />
          </div>
          
          <div>
            <Label>Address</Label>
            <Input
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              placeholder="Optional"
            />
          </div>
          
          <div>
            <Label>Notes</Label>
            <Textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Optional notes..."
              rows={2}
            />
          </div>
        </div>
        
        <DialogFooter className="flex justify-between">
          {appointment && (
            <Button 
              variant="destructive" 
              onClick={() => {
                onDelete(appointment.appointment_id);
                onClose();
              }}
            >
              <Trash2 className="h-4 w-4 mr-1" />
              Cancel Appointment
            </Button>
          )}
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? "Saving..." : "Save"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Todo Modal Component
function TodoModal({ open, onClose, todo, onSave }) {
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    priority: "medium",
    due_date: ""
  });
  const [saving, setSaving] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  
  const token = localStorage.getItem("token");
  
  useEffect(() => {
    if (todo) {
      setFormData({
        title: todo.title || "",
        description: todo.description || "",
        priority: todo.priority || "medium",
        due_date: todo.due_date || ""
      });
      setSelectedDate(todo.due_date ? parseISO(todo.due_date) : null);
    } else {
      setFormData({
        title: "",
        description: "",
        priority: "medium",
        due_date: ""
      });
      setSelectedDate(null);
    }
  }, [todo, open]);
  
  const handleSave = async () => {
    if (!formData.title) {
      toast.error("Please enter a title");
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        ...formData,
        due_date: selectedDate ? selectedDate.toISOString() : null
      };
      
      const url = todo 
        ? `${API}/dashboard/todos/${todo.todo_id}`
        : `${API}/dashboard/todos`;
      
      const res = await fetch(url, {
        method: todo ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      
      if (res.ok) {
        toast.success(todo ? "Task updated" : "Task created");
        onSave();
        onClose();
      } else {
        toast.error("Failed to save task");
      }
    } catch (error) {
      toast.error("Error saving task");
    } finally {
      setSaving(false);
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {todo ? "Edit Task" : "New Task"}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div>
            <Label>Title *</Label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="What needs to be done?"
            />
          </div>
          
          <div>
            <Label>Description</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Additional details..."
              rows={3}
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Priority</Label>
              <Select 
                value={formData.priority} 
                onValueChange={(v) => setFormData({ ...formData, priority: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRIORITY_OPTIONS.map((p) => (
                    <SelectItem key={p.value} value={p.value}>
                      <span className={p.color.split(' ')[0]}>{p.label}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Due Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {selectedDate ? format(selectedDate, "d MMM") : "Optional"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={setSelectedDate}
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
